<template>
  <page-bar back @back="back">
    <v-btn
      icon="mdi-share-variant"
      @click="openShareDialog"
      v-if="recipe && canEdit"
      aria-label="Share recipe"
    ></v-btn>
    <v-btn
      icon="mdi-delete"
      @click="deleteDialog = true"
      v-if="recipe && canEdit"
      aria-label="Delete recipe"
    ></v-btn>
    <v-btn
      icon="mdi-pencil"
      @click="editDialog = true"
      v-if="recipe && canEdit"
      aria-label="Edit recipe"
    ></v-btn>
  </page-bar>
  <v-container v-if="!recipe">
    <v-alert
      type="error"
      icon="mdi-alert-circle-outline"
      title="Not Found!"
      text="Oh oh, it seems like this is not an existing recipe!"
    />
  </v-container>
  <!-- Wide enough for two columns, no wider -->
  <v-container v-if="recipe" max-width="1200">
    <v-card class="recipe">
      <v-carousel
        v-if="images.length > 1"
        height="240"
        show-arrows="hover"
        hide-delimiter-background
      >
        <v-carousel-item
          v-for="(src, index) in images"
          :key="src"
          :src="src"
          :alt="`Picture ${index + 1} of ${images.length}`"
          cover
        />
      </v-carousel>
      <v-skeleton-loader
        v-else
        :loading="!recipe.image"
        type="image"
        :boilerplate="true"
      >
        <v-img
          :lazy-src="recipe.image"
          :src="recipe.image"
          alt=""
          cover
          height="200px"
        ></v-img>
      </v-skeleton-loader>

      <v-card-title>{{ recipe?.title }}</v-card-title>

      <v-card-subtitle v-if="recipe.preparationTime">
        {{ recipe.preparationTime }} minutes
        <v-icon icon="mdi-clock-outline" />
      </v-card-subtitle>

      <v-card-subtitle>
        <recipe-rating :recipe="recipe" @update-rating="submit" />
      </v-card-subtitle>

      <v-card-text>{{ recipe.subTitle }}</v-card-text>
      <v-card-text v-if="nutrition.length" class="pt-0">
        <span class="text-medium-emphasis">Per serving:</span>
        {{ nutrition.join(' · ') }}
      </v-card-text>
      <v-card-text v-if="source" class="pt-0">
        <span class="text-medium-emphasis">Source:</span>{{ ' '
        }}<a :href="source.href" target="_blank" rel="noopener noreferrer">
          {{ source.label }}
        </a>
      </v-card-text>
      <v-card-text v-if="recipe.tags?.length" class="pt-0">
        <v-chip v-for="tag in recipe.tags" :key="tag" class="mr-1">
          {{ tag }}
        </v-chip>
      </v-card-text>
      <template v-if="recipe.notes">
        <v-card-title>Notes:</v-card-title>
        <v-card-text class="notes">{{ recipe.notes }}</v-card-text>
      </template>
      <v-divider />
      <!-- Ingredients first (what to get out), then the steps; side by side
           when there is room, with the ingredients staying in view -->
      <div class="recipe-body">
        <section class="ingredients" aria-labelledby="ingredients-title">
          <div class="d-flex flex-wrap align-center justify-space-between pr-4">
            <v-card-title id="ingredients-title" tag="h2">
              Ingredients:
            </v-card-title>
            <servings-selector v-model="servings" />
          </div>
          <v-card-text>
            <v-list lines="two" bg-color="transparent">
              <v-list-item
                v-for="(ingredient, index) in ingredients"
                :key="index"
                :title="ingredient.text"
                :subtitle="ingredient.amount"
              />
            </v-list>
          </v-card-text>
          <v-card-actions v-if="recipe.ingredients.length">
            <v-btn
              prepend-icon="mdi-cart-plus"
              :loading="addingToGroceries"
              @click="addToGroceries"
            >
              Add to grocery list
            </v-btn>
          </v-card-actions>
        </section>
        <section class="steps" aria-labelledby="steps-title">
          <div class="d-flex flex-wrap align-center justify-space-between pr-4">
            <v-card-title id="steps-title" tag="h2">How to make:</v-card-title>
            <v-btn
              v-if="recipe.steps.length"
              prepend-icon="mdi-chef-hat"
              variant="tonal"
              @click="cooking = true"
            >
              Start cooking
            </v-btn>
          </div>
          <v-card-text>
            <v-card
              elevation="0"
              color="transparent"
              v-for="(step, index) in recipe.steps"
              :key="index"
            >
              <v-card-subtitle>Step {{ index + 1 }}</v-card-subtitle>
              <v-card-text>
                {{ step.text }}
              </v-card-text>
            </v-card>
          </v-card-text>
        </section>
      </div>
    </v-card>
    <cooking-mode
      :open="cooking"
      :title="recipe.title"
      :steps="recipe.steps"
      :ingredients="ingredients"
      @close="cooking = false"
    />
    <v-snackbar v-model="addedToGroceries" timeout="6000">
      Added {{ addedCount }}
      {{ addedCount === 1 ? 'ingredient' : 'ingredients' }} to the grocery list
      <template v-slot:actions>
        <v-btn variant="text" @click="undoAddToGroceries"> Undo </v-btn>
      </template>
    </v-snackbar>
    <delete-dialog
      :open="deleteDialog"
      @close="deleteDialog = false"
      @remove="remove"
    />
    <recipe-dialog
      v-if="recipe"
      :tag-suggestions="tagSuggestions"
      :open="editDialog"
      :recipe="recipe"
      :loading="loading"
      @submit="submit"
      @close="editDialog = false"
    />
    <ShareDialog
      v-if="recipe"
      :recipe="recipe"
      :share="share"
      :share-dialog="shareDialog"
      :shared-with-profiles="sharedWithProfiles"
      @close="shareDialog = false"
      @generate-recipe-invite="generateRecipeInvite"
      @remove-share="removeShare"
    />
  </v-container>
</template>

<script lang="ts">
  import { computed, defineComponent, type PropType, ref, watch } from 'vue';
  import RecipeDialog from '@/components/recipe/recipe-dialog/RecipeDialog.vue';
  import DeleteDialog from '@/components/delete-dialog/delete-dialog.vue';
  import RecipeRating from '@/components/recipe/recipe-rating/RecipeRating.vue';
  import type { Recipe, RecipePayload } from '@/service/recipe/types';
  import { useRecipeStore } from '@/stores/recipe';
  import { useRouter } from 'vue-router';
  import type { ClientInterface } from '@/service/types';
  import type InviteClient from '@/service/invite/inviteClient';
  import type GroupService from '@/service/group/groupService';
  import type { Profile } from '@/service/profile/types';
  import type RecipeClient from '@/service/recipe/recipeClient';
  import ShareDialog from '@/components/recipe/share-dialog/ShareDialog.vue';
  import ServingsSelector from '@/components/recipe/servings-selector/ServingsSelector.vue';
  import CookingMode from '@/components/recipe/cooking-mode/CookingMode.vue';
  import PageBar from '@/components/infrastructure/page-bar/PageBar.vue';
  import {
    scaleAmount,
    scaleIngredients,
    servingsOf,
  } from '@/service/recipe/scaling';
  import type { GroceryClientInterface } from '@/service/groceries/groceryClient';
  import { allTags } from '@/service/recipe/findRecipes';
  import {
    imagesOf,
    nutritionFacts,
    safeUrl,
    sourceLabel,
  } from '@/service/recipe/metadata';
  import {
    type GroceryPlan,
    planAdditions,
  } from '@/service/groceries/planAdditions';
  import { formatQuantity } from '@/service/recipe/units';
  import { useSettingStore } from '@/stores/settings';
  import { arrayRemove, doc, updateDoc } from 'firebase/firestore';
  import { useFirestore } from 'vuefire';

  export default defineComponent({
    name: 'DetailView',
    components: {
      ShareDialog,
      DeleteDialog,
      RecipeDialog,
      RecipeRating,
      ServingsSelector,
      CookingMode,
      PageBar,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      client: {
        type: Object as PropType<ClientInterface<Recipe, RecipePayload>>,
        required: true,
      },
      inviteClient: {
        type: Object as PropType<InviteClient>,
        required: true,
      },
      groupService: {
        type: Object as PropType<GroupService>,
        required: true,
      },
      groceryClient: {
        type: Object as PropType<GroceryClientInterface>,
        required: true,
      },
    },
    setup(props) {
      const router = useRouter();
      const firestore = useFirestore();
      const store = useRecipeStore(props.client);
      const recipe = store.getRecipe(props.id);
      const allRecipes = store.getRecipes();
      const loading = ref(false);
      const editDialog = ref(false);
      const deleteDialog = ref(false);
      const shareDialog = ref(false);
      const sharedWithProfiles = ref<Profile[]>([]);

      // Ingredient amounts follow the chosen servings; reset whenever the
      // recipe's own servings load or change
      const servings = ref(0);
      watch(
        () => recipe.value && servingsOf(recipe.value),
        (saved) => {
          servings.value = saved ?? 0;
        },
        { immediate: true },
      );
      const settingStore = useSettingStore();
      const ingredients = computed(() => {
        if (!recipe.value) return [];
        const saved = servingsOf(recipe.value);
        return recipe.value.ingredients.map(({ text, amount, unit }) => ({
          text,
          amount: formatQuantity(
            scaleAmount(amount, saved, servings.value),
            unit,
            settingStore.unitSystem,
          ),
        }));
      });

      // Cooking mode uses the ingredients at the chosen servings
      const cooking = ref(false);
      const addingToGroceries = ref(false);
      const addedToGroceries = ref(false);
      const addedCount = ref(0);
      let lastAdded: GroceryPlan | undefined;

      const canEdit = computed(() => {
        if (!recipe.value) return false;
        const recipeClient = props.client as RecipeClient;
        if (typeof recipeClient.canEdit === 'function') {
          return recipeClient.canEdit(recipe.value);
        }
        return true;
      });

      const source = computed(() => {
        const href = safeUrl(recipe.value?.sourceUrl);
        return href ? { href, label: sourceLabel(href) } : undefined;
      });

      return {
        recipe,
        images: computed(() => (recipe.value ? imagesOf(recipe.value) : [])),
        nutrition: computed(() => nutritionFacts(recipe.value?.nutrition)),
        source,
        tagSuggestions: computed(() => allTags(allRecipes.value)),
        servings,
        ingredients,
        cooking,
        addingToGroceries,
        addedToGroceries,
        addedCount,
        // Adds the ingredients at the chosen servings
        async addToGroceries() {
          if (!recipe.value) return;
          addingToGroceries.value = true;
          const scaled = scaleIngredients(
            recipe.value.ingredients,
            servingsOf(recipe.value),
            servings.value,
          );
          const recipeId = recipe.value.id;
          const plan = planAdditions(
            await props.groceryClient.get(),
            scaled.map((ingredient) => ({ ...ingredient, recipeId })),
          );
          await props.groceryClient.apply(plan);
          lastAdded = plan;
          addedCount.value = scaled.filter(({ text }) => text.trim()).length;
          addingToGroceries.value = false;
          addedToGroceries.value = true;
        },
        async undoAddToGroceries() {
          addedToGroceries.value = false;
          if (lastAdded) {
            await props.groceryClient.revert(lastAdded);
            lastAdded = undefined;
          }
        },
        loading,
        editDialog,
        deleteDialog,
        shareDialog,
        canEdit,
        sharedWithProfiles,
        async openShareDialog() {
          shareDialog.value = true;
          if (recipe.value?.sharedWith?.length) {
            sharedWithProfiles.value =
              await props.groupService.getProfilesByUids(
                recipe.value.sharedWith,
              );
          }
        },
        async share(shared: boolean) {
          loading.value = true;
          await store.update({
            recipe: { ...recipe.value!, shared },
            images: [],
          });
          loading.value = false;
          shareDialog.value = false;
        },
        async generateRecipeInvite(callback: (url: string) => void) {
          const invite = await props.inviteClient.createRecipeInvite(props.id);
          const url = `${location.origin}/invite/${invite.id}`;
          callback(url);
        },
        async removeShare(uid: string) {
          await updateDoc(doc(firestore, 'recipes', props.id), {
            sharedWith: arrayRemove(uid),
          });
          sharedWithProfiles.value = sharedWithProfiles.value.filter(
            (p) => p.uid !== uid,
          );
        },
        async submit(payload: RecipePayload) {
          loading.value = true;
          await store.update(payload);
          loading.value = false;
          editDialog.value = false;
        },
        async remove() {
          await store.remove(props.id);
          await router.push({ name: 'recipe' });
        },
        async back() {
          await router.push({ name: 'recipe' });
        },
      };
    },
  });
</script>

<style scoped>
  /* The ingredients stay in view while the steps scroll, which needs the
     card not to clip; its picture keeps the rounded corners itself */
  .recipe {
    overflow: visible;
  }

  .recipe > :first-child {
    border-radius: 12px 12px 0 0;
    overflow: hidden;
  }

  .recipe-body {
    container-type: inline-size;
    display: grid;
  }

  @container (min-width: 760px) {
    .recipe-body > .ingredients,
    .recipe-body > .steps {
      grid-row: 1;
    }

    .recipe-body {
      grid-template-columns: minmax(0, 1fr) 360px;
    }

    .steps {
      grid-column: 1;
    }

    .ingredients {
      grid-column: 2;
      position: sticky;
      top: 64px;
      align-self: start;
      border-left: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  .notes {
    white-space: pre-line;
  }
</style>
